import { type ReactElement, type ReactNode } from "react";
import { ToolCategory, ToolType } from "./ToolType";
import {
  CARRIAGES_CONFIGURATOR_URL,
  GEARBOX_SELECTION_ASSISTANT_URL,
  HEATER_SELECTION_ASSISTANT_URL,
  HOUSING_SELECTION_ASSISTANT_URL,
  INCH_SHAFT_CONFIGURATOR_URL,
  LINEAR_CONFIGURATOR_URL,
  LUBRICATION_SELECTION_ASSISTANT_URL,
  METRIC_SHAFT_CONFIGURATOR_URL,
  RAILS_CONFIGURATOR_URL,
  SELECTION_ASSISTANT,
} from "../../utils/url/staticUrls";
import { resolveCdnImageURL } from "../../utils/url/urlResolver";
import { Trans } from "react-i18next";
import { toMapFn } from "../../utils/reducers";

interface CommonToolConfig {
  type: ToolType;
  url: string;
  imageUrl: string;
  pdpLaunchLabel: ReactNode;
  title: ReactElement;
  name: ReactElement;
}

export type LaunchToolConfig = LaunchAssistantConfig | LaunchConfiguratorConfig;

// ReactElement has props on it's type, which we're using to access the i18nKey on the SearchRelatedResults component
export interface LaunchConfiguratorConfig extends CommonToolConfig {
  category: ToolCategory.Configurator;
  launchLabel: ReactElement;
}

export interface LaunchAssistantConfig extends CommonToolConfig {
  category: ToolCategory.Assistant;
  launchLabel: ReactElement;
}

export function isAssistantConfig(config: LaunchToolConfig): config is LaunchAssistantConfig {
  return config.category === ToolCategory.Assistant;
}
export function isConfiguratorConfig(config: LaunchToolConfig): config is LaunchConfiguratorConfig {
  return config.category === ToolCategory.Configurator;
}

const CONFIG_LIST: LaunchToolConfig[] = [
  {
    type: ToolType.System,
    category: ToolCategory.Configurator,
    url: LINEAR_CONFIGURATOR_URL,
    imageUrl: resolveCdnImageURL("configure_system.png"),
    launchLabel: <Trans i18nKey={"feature.launchConfigurator.system.launch"} />,
    pdpLaunchLabel: <Trans i18nKey={"feature.launchConfigurator.generic.launch"} />,
    title: <Trans i18nKey={"feature.launchConfigurator.system.title"} />,
    name: <Trans i18nKey={"cmsnavigationnode.schaefflerContentCatalog.FlyoutLinearConfiguratorNavNodetranslation"} />,
  },
  {
    type: ToolType.Rail,
    category: ToolCategory.Configurator,
    url: RAILS_CONFIGURATOR_URL,
    imageUrl: resolveCdnImageURL("configure_rail.png"),
    launchLabel: <Trans i18nKey={"feature.launchConfigurator.rail.launch"} />,
    pdpLaunchLabel: <Trans i18nKey={"feature.launchConfigurator.generic.launch"} />,
    title: <Trans i18nKey={"feature.launchConfigurator.rail.title"} />,
    name: <Trans i18nKey={"cmsnavigationnode.schaefflerContentCatalog.cmsitem.00117271translation"} />,
  },
  {
    type: ToolType.Carriage,
    category: ToolCategory.Configurator,
    url: CARRIAGES_CONFIGURATOR_URL,
    imageUrl: resolveCdnImageURL("configure_carriage.png"),
    launchLabel: <Trans i18nKey={"feature.launchConfigurator.carriage.launch"} />,
    pdpLaunchLabel: <Trans i18nKey={"feature.launchConfigurator.generic.launch"} />,
    title: <Trans i18nKey={"feature.launchConfigurator.carriage.title"} />,
    name: <Trans i18nKey={"page.configurator.carriage"} />,
  },
  {
    type: ToolType.Rotary,
    category: ToolCategory.Assistant,
    url: SELECTION_ASSISTANT,
    imageUrl: "https://cdn.schaeffler-ecommerce.com/frontend/Rillenkugellager_00014D0C_DGBB.png",
    launchLabel: <Trans i18nKey={"feature.launchConfigurator.rotaryAssistant.launch"} />,
    pdpLaunchLabel: <Trans i18nKey={"feature.launchConfigurator.generic.launchAssistant"} />,
    title: <Trans i18nKey={"feature.launchConfigurator.rotaryAssistant.title"} />,
    name: (
      <Trans i18nKey={"cmsnavigationnode.schaefflerContentCatalog.BearingSelectionAssistantKSNavNodetranslation"} />
    ),
  },
  {
    type: ToolType.Housing,
    category: ToolCategory.Assistant,
    url: HOUSING_SELECTION_ASSISTANT_URL,
    imageUrl: "https://cdn.schaeffler-ecommerce.com/cdn/0001ACB0_d.png",
    launchLabel: <Trans i18nKey={"feature.launchConfigurator.housingAssistant.launch"} />,
    pdpLaunchLabel: <Trans i18nKey={"feature.launchConfigurator.generic.launchAssistant"} />,
    title: <Trans i18nKey={"feature.launchConfigurator.housingAssistant.title"} />,
    name: <Trans i18nKey={"cmsnavigationnode.schaefflerContentCatalog.SelectionAssistantHousingNavNodetranslation"} />,
  },
  {
    type: ToolType.Heater,
    category: ToolCategory.Assistant,
    url: HEATER_SELECTION_ASSISTANT_URL,
    imageUrl: "https://cdn.schaeffler-ecommerce.com/frontend/Heater.jpg",
    launchLabel: <Trans i18nKey={"feature.launchConfigurator.heaterAssistant.launch"} />,
    pdpLaunchLabel: <Trans i18nKey={"feature.launchConfigurator.generic.launchAssistant"} />,
    title: <Trans i18nKey={"feature.launchConfigurator.heaterAssistant.title"} />,
    name: <Trans i18nKey={"cmsnavigationnode.schaefflerContentCatalog.HeatingManagerNavNodetranslation"} />,
  },
  {
    type: ToolType.Gearbox,
    category: ToolCategory.Assistant,
    url: GEARBOX_SELECTION_ASSISTANT_URL,
    imageUrl: "",
    launchLabel: <Trans i18nKey={"feature.launchConfigurator.gearboxAssistant.launch"} />,
    pdpLaunchLabel: <Trans i18nKey={"feature.launchConfigurator.generic.launchAssistant"} />,
    title: <Trans i18nKey={"feature.launchConfigurator.gearboxAssistant.title"} />,
    name: <Trans i18nKey={"page.sa.gearbox.title"} />,
  },
  {
    type: ToolType.Lubrication,
    category: ToolCategory.Assistant,
    url: LUBRICATION_SELECTION_ASSISTANT_URL,
    imageUrl: "",
    launchLabel: <Trans i18nKey={"feature.launchConfigurator.lubricationAssistant.launch"} />,
    pdpLaunchLabel: <Trans i18nKey={"feature.launchConfigurator.generic.launchAssistant"} />,
    title: <Trans i18nKey={"feature.launchConfigurator.lubricationAssistant.title"} />,
    name: <Trans i18nKey={"breadcrumb.lubrication.selection.assistant"} />,
  },
  {
    type: ToolType.MetricShaft,
    category: ToolCategory.Configurator,
    url: METRIC_SHAFT_CONFIGURATOR_URL,
    imageUrl: "",
    launchLabel: <Trans i18nKey={"feature.launchConfigurator.metricShaft.launch"} />,
    pdpLaunchLabel: <Trans i18nKey={"feature.launchConfigurator.generic.launchAssistant"} />,
    title: <Trans i18nKey={"feature.launchConfigurator.metricShaft.title"} />,
    name: <Trans i18nKey={"configurator.shaft.title.metric"} />,
  },
  {
    type: ToolType.InchShaft,
    category: ToolCategory.Configurator,
    url: INCH_SHAFT_CONFIGURATOR_URL,
    imageUrl: "",
    launchLabel: <Trans i18nKey={"feature.launchConfigurator.inchShaft.launch"} />,
    pdpLaunchLabel: <Trans i18nKey={"feature.launchConfigurator.generic.launchAssistant"} />,
    title: <Trans i18nKey={"feature.launchConfigurator.inchShaft.title"} />,
    name: <Trans i18nKey={"configurator.shaft.title.inch"} />,
  },
];

const TOOL_CONFIG: Dict<LaunchToolConfig> = toMapFn(CONFIG_LIST, (item) => item.type);

export function getToolConfiguration(type: ToolType): LaunchToolConfig {
  if (import.meta.env.DEV) {
    if (!TOOL_CONFIG[type]) {
      throw new Error("Unsupported value of ToolType");
    }
  }
  return TOOL_CONFIG[type];
}
